import { esteNecesarTeren } from '@b4l/shared'
import { useState } from 'react'
import type { PunctLucru } from './PunctLucruSelect'
import { FarmaciaSiPunctul } from './FarmaciaSiPunctul'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Gift, Check } from 'lucide-react'
import { api } from '../../lib/api'
import { Field } from '../ui/Field'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import type { Kit } from './SamplingKituri'

/**
 * Pornirea unei acțiuni, în doi pași: unde, apoi ce se dă.
 *
 * Formularul de dinainte cerea totul deodată, iar rezultatul se vedea: la prima acțiune reală au
 * ajuns în „Denumire" numele unei persoane, iar produsele au rămas nebifate — acțiunea părea
 * gata, dar codul QR nu deschidea nimic.
 *
 * Aici pasul doi nu se poate sări fără produse, iar tot ce se poate deduce singur (agentul, data,
 * denumirea la farmacie) e completat dinainte și doar arătat.
 *
 * Tombola sare peste pasul doi: nu predă nimic.
 */

type Tip = 'farmacie' | 'eveniment' | 'tombola' | 'promo'

interface ProdusCatalog {
  id: number
  sku: string
  name: string
}

interface ContainerScurt {
  id: number
  cod: string
  titlu: string
}

interface Props {
  tip: Tip
  onClose: () => void
  onCreat: (actiuneId: number) => void
  /**
   * Activitatea de care ține acțiunea, când wizardul se deschide DIN ea.
   *
   * Pornit din fila Teren a unui eveniment, containerul e deja cunoscut — a-l cere din nou ar
   * însemna să alegi din listă chiar lucrul din care tocmai ai plecat.
   */
  campanieInitiala?: number | null
}

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

function aziIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function lunaCurenta(): string {
  return new Date().toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' })
}

const TITLU: Record<Tip, string> = {
  farmacie: 'Sampling în farmacie',
  eveniment: 'Sampling la eveniment',
  tombola: 'Tombolă',
  promo: 'Predare promoțională',
}

export function SamplingWizard({ tip, onClose, onCreat, campanieInitiala }: Props) {
  const queryClient = useQueryClient()
  const [pas, setPas] = useState<1 | 2>(1)

  const [denumire, setDenumire] = useState('')
  const [locatie, setLocatie] = useState('')
  const [data, setData] = useState(aziIso())
  const [punct, setPunct] = useState<PunctLucru | null>(null)
  const [pharmaClientId, setPharmaClientId] = useState<number | null>(null)
  const [agentUserId, setAgentUserId] = useState<number | null>(null)
  /**
   * Containerul de care tine actiunea. La farmacie si promotional serverul completeaza singur
   * programul curent, deci campul poate ramane gol. La eveniment si tombola trebuie ales:
   * acolo containerul are nume propriu.
   */
  const [campanieId, setCampanieId] = useState<number | null>(campanieInitiala ?? null)
  /*
    La actiunea promotionala bonul pleaca la fiecare predare, nu la inchidere — si nu e o
    preferinta, e o necesitate: actiunea e permanenta, nu se inchide niciodata, deci un bon
    „la inchidere" n-ar pleca nicaieri. Marfa ar iesi din depozit fara sa iasa din gestiune.
  */
  const [modBon, setModBon] = useState<'per_pv' | 'per_actiune'>(
    tip === 'eveniment' ? 'per_actiune' : 'per_pv',
  )

  const [kitId, setKitId] = useState<number | null>(null)
  const [selectie, setSelectie] = useState<Record<number, number>>({})
  /**
   * Acțiune de colectare: se strâng contacte și acorduri, nu se dă nimic.
   *
   * Alexandru: „aici avem nevoie să adăugăm opțiune să colectăm date și fără distribuire
   * produse". Se întâmplă la o farmacie unde agentul stă de vorbă și strânge înscrieri.
   *
   * E o bifă, nu un tip nou de acțiune: rămâne farmacie sau eveniment, cu locația și raportarea
   * ei. Se schimbă doar ce decurge din predare — produse, semnătură, PV, bon de consum.
   */
  const [faraMostre, setFaraMostre] = useState(false)

  const agenti = useQuery({
    queryKey: ['sampling', 'agenti'],
    queryFn: async () =>
      (await api.get<{ id: number; name: string }[]>('/marketing/sampling/agenti')).data,
  })

  const kituri = useQuery({
    queryKey: ['sampling', 'kituri'],
    queryFn: async () => (await api.get<Kit[]>('/marketing/sampling/kituri')).data,
    enabled: tip !== 'tombola',
  })

  const catalog = useQuery({
    queryKey: ['sampling', 'produse'],
    queryFn: async () =>
      (await api.get<{ produse: ProdusCatalog[] }>('/marketing/sampling/produse')).data,
    enabled: tip !== 'tombola',
  })

  const containere = useQuery<{ campanii: ContainerScurt[] }>({
    queryKey: ['campanii-deschise'],
    queryFn: async () =>
      (await api.get<{ campanii: ContainerScurt[] }>('/campanii', { params: { deschise: 'true' } }))
        .data,
  })
  const cereContainer = tip === 'eveniment' || tip === 'tombola'

  const creeaza = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ id: number }>('/marketing/sampling/actiuni', {
        denumire: denumire.trim(),
        tip,
        locatie: locatie.trim() || null,
        pharmaClientId: tip === 'farmacie' ? pharmaClientId : null,
        branchId: tip === 'farmacie' ? (punct?.id ?? null) : null,
        agentUserId,
        dataStart: data || null,
        dataFinal: null,
        modBon: tip === 'tombola' ? undefined : tip === 'promo' ? 'per_pv' : modBon,
        kitId: tip === 'tombola' || faraMostre ? null : kitId,
        faraMostre,
        campanieId,
      })
      // Produsele bifate manual se pun după creare: acțiunea vine deja cu liniile kitului,
      // iar un `PUT` peste ele e singurul loc unde cele două variante se întâlnesc.
      if (tip !== 'tombola' && !faraMostre && !kitId) {
        await api.put(`/marketing/sampling/actiuni/${r.data.id}/produse`, {
          linii: Object.entries(selectie).map(([pid, cant]) => ({
            productId: Number(pid),
            cantitate: cant,
            activ: true,
          })),
        })
      }
      return r.data
    },
    onSuccess: (a) => {
      toast.success('Acțiune pornită')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
      onCreat(a.id)
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  // Punctul de lucru NU e obligatoriu, desi e calea normala. In evidenta sunt doar farmaciile
  // aduse din ExpertAccounts; un lant cu care nu lucram direct pur si simplu nu e acolo, iar
  // o actiune n-are voie sa fie blocata de lipsa unui rand intr-o tabela sincronizata.
  const undeGata =
    denumire.trim().length >= 3 && Boolean(data) && (!cereContainer || campanieId !== null)
  const ceGata =
    tip === 'tombola' || faraMostre || kitId !== null || Object.keys(selectie).length > 0

  const numeAgent =
    agentUserId === null
      ? 'Eu'
      : ((agenti.data ?? []).find((u) => u.id === agentUserId)?.name ?? '')

  return (
    <Modal open onClose={onClose} size="lg" title={TITLU[tip]} inchideLaClicInAfara={false}>
      {/*
        Tombola n-are pasul doi — nu se da nimic. Indicatorul de pasi ii spunea omului ca
        urmeaza ceva ce nu urmeaza, si facea sa para proces ce e un singur camp.
      */}
      {tip !== 'tombola' && (
        <div className="mb-4 flex items-center gap-2 text-xs">
          <Pas numar={1} activ={pas === 1} gata={undeGata} eticheta="Unde" />
          <div className="h-px flex-1 bg-b4l-border" />
          <Pas numar={2} activ={pas === 2} gata={ceGata} eticheta="Ce oferi" />
        </div>
      )}

      {pas === 1 ? (
        <div className="space-y-3">
          {tip === 'farmacie' && (
            <>
              <FarmaciaSiPunctul
                clientId={pharmaClientId}
                punct={punct}
                onClient={setPharmaClientId}
                onPunct={(p) => {
                  setPunct(p)
                  if (p) {
                    setDenumire(`${p.eticheta} — ${lunaCurenta()}`)
                    if (!locatie.trim()) setLocatie(p.adresa ?? p.oras ?? '')
                  }
                }}
                etichetaClient="Lanț / client (opțional)"
                ajutorClient="Alege-l dacă vrei să cauți doar printre punctele lui. Se completează și singur, când punctul ales are lanțul în evidență. Dacă nu e în CRM, îl aduci din ExpertAccounts sau îl adaugi."
                ajutorPunct="Caută după numele farmaciei sau după oraș. Restul se completează de aici. Un punct care lipsește se adaugă cu „+ Punct nou” (după ce alegi clientul); altfel sari peste și scrie denumirea de mână."
              />
            </>
          )}

          <Field
            label={tip === 'farmacie' ? 'Denumirea acțiunii' : 'Nume'}
            value={denumire}
            onChange={(e) => setDenumire(e.target.value)}
            placeholder={
              tip === 'eveniment'
                ? 'Ex.: Congres Farmacia Viitorului'
                : tip === 'tombola'
                  ? 'Ex.: Tombolă Bucharest Fitness Festival'
                  : 'Ex.: Catena — Barbu Văcărescu — august 2026'
            }
            hint="Apare pe afiș, pe formular și pe procesul-verbal."
          />

          {tip !== 'farmacie' && (
            <Field
              label="Locație"
              value={locatie}
              onChange={(e) => setLocatie(e.target.value)}
              placeholder="Ex.: Hala Laminor, București"
            />
          )}

          <Field label="Data" type="date" value={data} onChange={(e) => setData(e.target.value)} />

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Ține de{cereContainer ? '' : ' (opțional)'}</span>
            <select
              value={campanieId ?? ''}
              onChange={(e) => setCampanieId(e.target.value ? Number(e.target.value) : null)}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              <option value="">
                {cereContainer ? '— alege evenimentul —' : 'Programul curent, completat automat'}
              </option>
              {(containere.data?.campanii ?? [])
                // „Necesar teren” tine cereri de marfa, nu actiuni de teren.
                .filter((c) => !esteNecesarTeren(c.cod))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.cod} — {c.titlu}
                  </option>
                ))}
            </select>
            <span className="text-xs text-b4l-text-dim">
              {cereContainer
                ? 'O acțiune la un eveniment se leagă de evenimentul din PR & Evenimente.'
                : 'Lăsat gol, intră în programul de sampling al trimestrului.'}
            </span>
          </label>

          <div className="rounded-lg bg-b4l-bg p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-b4l-text-dim">
              Se completează automat
            </p>
            <dl className="mt-2 space-y-1 text-sm">
              <Rand eticheta="Agent" valoare={numeAgent} />
              <Rand eticheta="Cod QR" valoare="Generat la pornire" />
              {tip !== 'tombola' && (
                <Rand
                  eticheta="Bon de consum"
                  valoare={modBon === 'per_pv' ? 'Unul per PV' : 'Unul la închidere'}
                />
              )}
            </dl>

            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-b4l-blue">Schimbă</summary>
              <div className="mt-2 space-y-2">
                <label className="block">
                  <span className="text-xs text-b4l-text-muted">Agent</span>
                  <select
                    value={agentUserId ?? ''}
                    onChange={(e) => setAgentUserId(e.target.value ? Number(e.target.value) : null)}
                    className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                  >
                    <option value="">Eu</option>
                    {(agenti.data ?? []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </label>
                {/* La promoțional nu se alege: acțiunea nu se închide, deci bonul pleacă per PV. */}
                {tip !== 'tombola' && tip !== 'promo' && (
                  <label className="block">
                    <span className="text-xs text-b4l-text-muted">Bon de consum</span>
                    <select
                      value={modBon}
                      onChange={(e) => setModBon(e.target.value as 'per_pv' | 'per_actiune')}
                      className="mt-1 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                    >
                      <option value="per_pv">Unul per PV</option>
                      <option value="per_actiune">Unul la închiderea acțiunii</option>
                    </select>
                  </label>
                )}
              </div>
            </details>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-b4l-border bg-b4l-bg p-3">
            <input
              type="checkbox"
              checked={faraMostre}
              onChange={(e) => {
                setFaraMostre(e.target.checked)
                if (e.target.checked) {
                  setKitId(null)
                  setSelectie({})
                }
              }}
              className="mt-0.5 h-4 w-4"
            />
            <span className="text-sm">
              <span className="block font-medium text-b4l-text">
                Doar colectare de date, fără mostre
              </span>
              <span className="block text-xs text-b4l-text-muted">
                Se strâng contacte și acorduri, atât: nu se dă nicio mostră și nu iese nimic din
                gestiune. Lasă nebifat dacă se predau mostre — produsele se aleg mai jos, iar
                agentul le predă din „Predare la stand”.
              </span>
            </span>
          </label>

          {!faraMostre && (
            <p className="text-sm text-b4l-text-muted">
              Alege un kit gata configurat sau bifează produsele.
            </p>
          )}

          <div className={`space-y-2 ${faraMostre ? 'hidden' : ''}`}>
            {(kituri.data ?? [])
              .filter((k) => k.activ && (k.tip === 'orice' || k.tip === tip))
              .map((k) => (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => {
                    setKitId(k.id)
                    setSelectie({})
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left ${
                    kitId === k.id
                      ? 'border-b4l-green bg-b4l-green-light'
                      : 'border-b4l-border bg-white'
                  }`}
                >
                  <Gift size={18} className="shrink-0 text-b4l-text-muted" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-b4l-text">{k.denumire}</span>
                    <span className="block text-xs text-b4l-text-muted">
                      {k.linii.map((l) => `${l.denumire} ×${l.cantitate}`).join(', ')}
                    </span>
                  </span>
                  {kitId === k.id && <Check size={16} className="shrink-0 text-b4l-green-dark" />}
                </button>
              ))}
          </div>

          <details open={kitId === null} className={faraMostre ? 'hidden' : ''}>
            <summary className="cursor-pointer text-sm font-medium text-b4l-text">
              Alege produsele manual
            </summary>
            {!catalog.data?.produse.length ? (
              <p className="mt-2 text-sm text-b4l-amber-dark">
                Niciun produs bifat ca mostră (Sampling → Setări).
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {catalog.data.produse.map((p) => {
                  const ales = p.id in selectie
                  return (
                    <div key={p.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={ales}
                        onChange={(e) =>
                          setSelectie((s) => {
                            const copie = { ...s }
                            if (e.target.checked) copie[p.id] = s[p.id] ?? 1
                            else delete copie[p.id]
                            return copie
                          })
                        }
                        className="h-4 w-4"
                      />
                      <span className="flex-1">
                        {p.name}
                        <span className="ml-1 text-xs text-b4l-text-dim">{p.sku}</span>
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={selectie[p.id] ?? 1}
                        disabled={!ales}
                        onChange={(e) =>
                          setSelectie((s) => ({
                            ...s,
                            [p.id]: Math.max(1, Number(e.target.value) || 1),
                          }))
                        }
                        className="w-16 rounded border border-b4l-border px-2 py-1 text-sm disabled:opacity-40"
                      />
                    </div>
                  )
                })}
                {Object.keys(selectie).length > 0 && kitId !== null && (
                  <p className="text-xs text-b4l-amber-dark">
                    Ai și un kit ales. Bifele manuale îl înlocuiesc.
                  </p>
                )}
              </div>
            )}
          </details>
        </div>
      )}

      <div className="mt-4 flex justify-between gap-2">
        <Button variant="secondary" onClick={pas === 1 ? onClose : () => setPas(1)}>
          {pas === 1 ? 'Anulează' : 'Înapoi'}
        </Button>

        {pas === 1 && tip !== 'tombola' ? (
          <Button disabled={!undeGata} onClick={() => setPas(2)}>
            Continuă
          </Button>
        ) : (
          <Button
            disabled={!undeGata || !ceGata || creeaza.isPending}
            onClick={() => creeaza.mutate()}
          >
            {creeaza.isPending
              ? 'Se pornește…'
              : tip === 'tombola'
                ? 'Generează QR'
                : 'Pornește și generează QR'}
          </Button>
        )}
      </div>
    </Modal>
  )
}

function Pas({
  numar,
  activ,
  gata,
  eticheta,
}: {
  numar: number
  activ: boolean
  gata: boolean
  eticheta: string
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
          activ
            ? 'bg-b4l-blue text-white'
            : gata
              ? 'bg-b4l-green-light text-b4l-green-dark'
              : 'bg-b4l-bg text-b4l-text-dim'
        }`}
      >
        {gata && !activ ? <Check size={12} /> : numar}
      </span>
      <span className={activ ? 'font-medium text-b4l-text' : 'text-b4l-text-muted'}>
        {eticheta}
      </span>
    </span>
  )
}

function Rand({ eticheta, valoare }: { eticheta: string; valoare: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-b4l-text-muted">{eticheta}</dt>
      <dd className="text-right text-b4l-text">{valoare}</dd>
    </div>
  )
}
